"use client";

import { createContext, useState, useEffect, useContext, useRef } from "react";
import { useEffectOnce } from "@/hooks/useEffectOnce";
import { openDB } from "idb";
import { motion } from "motion/react";
import { Loader2 } from "lucide-react";

type FrameInfo = {
  frameIndex: number;
  loadBatch: number;
};

type FrameStore = {
  frameIndex: number;
  loaded: boolean;
  image: HTMLImageElement | null;
  failBackFrameIndex: number;
};

const totalFrames = 400;
const totalBatches = 11;

const DB_NAME = "animationCache";
const STORE_NAME = "frames-v1";

// Open IndexedDB
async function getDB() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
}

// Get Frame from IndexedDB
async function getFrameFromDB(index: number) {
  const db = await getDB();
  return db.get(STORE_NAME, index);
}

// Store Frame in IndexedDB
async function storeFrameInDB(index: number, base64Image: string) {
  const db = await getDB();
  await db.put(STORE_NAME, base64Image, index);
}

// Generate Image Path
function getCurrentFrame(index: number) {
  return `/images/animation/frame_${index.toString().padStart(5, "0")}.webp`;
}

// Initialize Frame Store
const frameStoreInit: FrameStore[] = [];
for (let i = 0; i < totalFrames; i++) {
  if (i === 0) {
    const newFrame: FrameStore = { frameIndex: i, loaded: false, image: null, failBackFrameIndex: totalFrames - 1 };
    frameStoreInit.push(newFrame);
  } else {
    const newFrame: FrameStore = { frameIndex: i, loaded: false, image: null, failBackFrameIndex: i - 1 };
    frameStoreInit.push(newFrame);
  }
}

// Setup Batches
const frameInfo: FrameInfo[] = [{ frameIndex: 0, loadBatch: 0 }];

for (let i = 10; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 1 });
}

for (let i = 5; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 2 });
}

for (let i = 3; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 3 });
}

for (let i = 8; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 4 });
}

for (let i = 2; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 5 });
}

for (let i = 7; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 6 });
}

for (let i = 4; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 7 });
}

for (let i = 9; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 8 });
}

for (let i = 1; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 9 });
}

for (let i = 6; i < totalFrames; i += 10) {
  frameInfo.push({ frameIndex: i, loadBatch: 10 });
}

function getFrameImage(index: number, frameStore: FrameStore[]) {
  const frame = frameStore.find((f) => f.frameIndex === index);
  if (!frame) {
    return getFrameImage(0, frameStore);
  }
  if (!frame.loaded || !frame.image) {
    return getFrameImage(frame.failBackFrameIndex, frameStore);
  }
  if (frame.frameIndex === 0 && (!frame.loaded || !frame.image)) {
    return null;
  }
  return frame.image;
}

// Setup Context
export const SiteGraphicAnimationContext = createContext<{
  loadedInitial: boolean;
  loaded: boolean;
  frameStore: FrameStore[];
  loadingDismissed: boolean;
  setLoadingDismissed: (loadingDismissed: boolean) => void;
}>({
  loadedInitial: false,
  loaded: false,
  frameStore: [],
  loadingDismissed: false,
  setLoadingDismissed: () => {},
});

// Provider
export function SiteGraphicAnimationProvider({ children }: { children: React.ReactNode }) {
  const [loadedInitial, setLoadedInitial] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [frameStore, setFrameStore] = useState<FrameStore[]>(frameStoreInit);
  const [loadingDismissed, setLoadingDismissed] = useState<boolean>(false);

  const updateFrameStoreWithBatch = (batchFrameStore: FrameStore[]) => {
    setFrameStore((prevFrameStore) => {
      const updatedFrameStore = [...prevFrameStore];
      batchFrameStore.forEach((frame) => {
        const index = updatedFrameStore.findIndex((f) => f.frameIndex === frame.frameIndex);
        if (index > -1) {
          updatedFrameStore[index] = frame;
        }
      });
      return updatedFrameStore;
    });
  };

  async function loadFrame(frameIndex: number): Promise<FrameStore> {
    // Try to get frame from IndexedDB first
    const cachedImage = await getFrameFromDB(frameIndex);
    if (cachedImage) {
      const img = new Image();
      img.src = cachedImage;
      return { frameIndex, loaded: true, image: img, failBackFrameIndex: frameIndex - 1 };
    }

    // Otherwise, load from network
    return new Promise((resolve) => {
      const img = new Image();
      img.src = getCurrentFrame(frameIndex);
      img.onload = async () => {
        // Convert image to base64 and store in IndexedDB
        const blob = await fetch(img.src).then((res) => res.blob());
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64Data = reader.result as string;
          await storeFrameInDB(frameIndex, base64Data);
          const base64Img = new Image();
          base64Img.src = base64Data;
          resolve({
            frameIndex,
            loaded: true,
            image: base64Img,
            failBackFrameIndex: frameStore.find((f) => f.frameIndex === frameIndex)?.failBackFrameIndex ?? 0,
          });
        };
      };
      img.onerror = () => {
        resolve({
          frameIndex,
          loaded: false,
          image: null,
          failBackFrameIndex: frameStore.find((f) => f.frameIndex === frameIndex)?.failBackFrameIndex ?? 0,
        });
      };
    });
  }

  function preloadImages() {
    const controller = new AbortController();
    const { signal } = controller;

    const preload = async () => {
      const preloadStartTime = new Date().getTime();
      for (let batchNum = 0; batchNum < totalBatches; batchNum++) {
        if (signal.aborted) {
          console.log("Preload aborted");
          return;
        }

        const batchStartTime = new Date().getTime();
        // Load batch
        const batch = frameInfo
          .filter((info) => info.loadBatch === batchNum)
          .filter((info) => {
            const frame = frameStore.find((f) => f.frameIndex === info.frameIndex);
            return !frame || !frame.loaded;
          });
        const batchPromises: Promise<FrameStore>[] = batch.map((frame) => loadFrame(frame.frameIndex));

        try {
          const batchImages = await Promise.all(batchPromises);
          updateFrameStoreWithBatch(batchImages);
          let endProgress = ` ${Math.round(((batchNum + 1) / totalBatches) * 100)}% (${batchNum + 1}/${totalBatches}), Batch ${batchNum + 1} loaded in ${new Date().getTime() - batchStartTime}ms`;
          if (endProgress.length < 50) {
            endProgress += " ".repeat(50 - endProgress.length);
          }
          console.info(
            "%c   Loading Animation: " +
              `%c${"#".repeat((batchNum + 1) * 3)}` +
              `%c${"-".repeat(totalBatches * 3 - (batchNum + 1) * 3)}` +
              `%c${endProgress}`,
            "background: #fc2f00; color: #efe1d5",
            "background: #ffff00; color: #ffff00",
            "background: #000000; color: #000000",
            "background: #0037ff; color: #efe1d5; width: 100%",
          );
          if (batchNum === 1) {
            setLoadedInitial(true);
          }
          if (batchNum === 5) {
            setLoaded(true);
            console.log(`Primary preload of images completed in ${new Date().getTime() - preloadStartTime}ms`);
          }
        } catch (error) {
          console.error("Batch loading aborted", error);
          return;
        }
      }
      console.log(`Preloaded images in ${new Date().getTime() - preloadStartTime}ms`);
    };

    preload();

    return () => controller.abort(); // Return the abort function
  }

  // Preload images
  useEffectOnce(() => {
    const abortPreload = preloadImages();

    return () => {
      console.log("Preload aborted");
      abortPreload();
    };
  });

  return (
    <SiteGraphicAnimationContext.Provider
      value={{
        loadedInitial,
        loaded,
        frameStore,
        loadingDismissed,
        setLoadingDismissed,
      }}
    >
      {children}
    </SiteGraphicAnimationContext.Provider>
  );
}

export function useSiteGraphicAnimation() {
  return useContext(SiteGraphicAnimationContext);
}

export function SiteGraphicAnimation() {
  const { loadedInitial, frameStore } = useSiteGraphicAnimation();
  const frameIndex = useRef<number>(0);
  const animationFrameId = useRef<number | null>(null);
  const imageContainer = useRef<HTMLImageElement>(null!);
  const lastFrameTime = useRef<number>(Date.now());
  const frameDuration: number = 1000 / 30; // 30 FPS

  // Animation function
  const animate = () => {
    const now = Date.now();
    const elapsed = now - lastFrameTime.current;

    if (elapsed > frameDuration) {
      lastFrameTime.current = now - (elapsed % frameDuration);
      const frame = getFrameImage(frameIndex.current, frameStore);
      if (imageContainer.current && frame && frame.complete) {
        imageContainer.current.src = frame.src;
      }
      frameIndex.current = frameIndex.current + 1 >= totalFrames ? 0 : frameIndex.current + 1;
    }

    if (imageContainer.current) {
      animationFrameId.current = requestAnimationFrame(animate);
    }
  };

  // Start animation once images are loaded
  useEffect(() => {
    if (loadedInitial) {
      lastFrameTime.current = Date.now(); // Reset timer when starting animation
      animationFrameId.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [loadedInitial, frameStore]); // eslint-disable-line react-hooks/exhaustive-deps

  //const firstFrame = frameStore.find((f) => f.frameIndex === 0);

  return (
    <>
      {!loadedInitial && (
        <div className="flex h-full w-full items-center justify-center">
          <Loader2 className="text-cool-gray-500 h-28 w-28 animate-spin opacity-90" />
        </div>
      )}
      {loadedInitial && imageContainer !== null && (
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: imageContainer.current?.src !== "" ? 1 : 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          ref={imageContainer}
          alt=""
          height="100%"
          width="100%"
          className="object-contain"
        />
      )}
    </>
  );
}
